import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageMeta from '../components/PageMeta';
import FadeIn from '../components/FadeIn';

const services = [
  {
    number: '01',
    title: 'Wedding Photography',
    tagline: 'Documentary coverage for your most important day',
    description:
      'Your wedding day deserves a photographer who understands that the best moments happen between the planned ones — in the quiet glance exchanged across a room, the spontaneous laugh that breaks through tears, the tender touch that nobody planned. Dhrumil\'s approach is documentary at heart: present, unobtrusive, and fully committed to capturing your day as it truly unfolds.',
    includes: [
      'Full-day coverage (8–12 hours)',
      'Second photographer available on request',
      'Online gallery with high-resolution downloads',
      'Print-ready files with no watermarks',
      'Complimentary engagement session',
      'Delivery within 4–6 weeks',
    ],
  },
  {
    number: '02',
    title: 'Wedding Videography',
    tagline: 'Cinematic films that bring your day back to life',
    description:
      'A wedding film captures what photography alone cannot — the tone of your vows, the spontaneous laughter during speeches, the sound of the room when you walk through the door. Working alongside the photography team, Dhrumil crafts short and feature-length films that feel like cinema, not a home video.',
    includes: [
      'Feature film (10–20 minutes)',
      'Highlight reel (3–5 minutes)',
      'Ceremony and speeches in full',
      'Professional colour grading',
      'Bespoke music licensing',
      'Private online delivery',
    ],
  },
  {
    number: '03',
    title: 'Engagement Shoots',
    tagline: 'Your story, before the big day',
    description:
      'An engagement session is a chance to meet in a relaxed setting, explore a location that means something to you both, and create images that are entirely yours — without the timeline and pressure of a wedding day. It\'s also the perfect opportunity to get comfortable in front of the camera and to see how beautifully your story translates into pictures.',
    includes: [
      '1–2 hour outdoor or location session',
      'Up to two outfit changes',
      'Online gallery with full-resolution images',
      'Styling and location guidance included',
      'Available throughout New Zealand',
    ],
  },
  {
    number: '04',
    title: 'Portrait Photography',
    tagline: 'Genuine, unhurried portraits made with care',
    description:
      'Every person has a story worth photographing. Whether you\'re looking for individual portraits, family images, or something in between, Dhrumil\'s portrait sessions are built around ease and authenticity. The goal is always an image that feels undeniably like you — at your best, and completely at ease.',
    includes: [
      'Up to 2 hours on location or in-studio',
      'Multiple looks or outfit changes',
      'Professional retouching included',
      'High-resolution digital delivery',
      'Suitable for personal, family, and lifestyle use',
    ],
  },
  {
    number: '05',
    title: 'Indian Weddings',
    tagline: 'Celebrating culture, ceremony, and colour',
    description:
      'South Asian weddings are among the most visually rich and emotionally layered celebrations in the world. Dhrumil brings both cultural fluency and artistic skill to every Indian wedding he photographs — understanding the significance of each ceremony, anticipating the moments that matter most, and ensuring that the full breadth of your celebration is documented with depth and respect.',
    includes: [
      'Multi-day coverage (Mehndi, Sangeet, Ceremony, Reception)',
      'Second photographer included',
      'Full ceremony coverage with detail shots',
      'Family portrait sessions',
      'Online gallery with high-resolution downloads',
      'Dedicated Indian wedding album design available',
    ],
  },
  {
    number: '06',
    title: 'Corporate Photography',
    tagline: 'Professional imagery that tells your brand story',
    description:
      'Great corporate photography is more than headshots. It\'s about capturing the culture, energy, and people behind a brand in a way that communicates authenticity and professionalism. Whether it\'s a team event, product launch, conference, or executive portrait session, Dhrumil approaches corporate work with the same deliberate craft he brings to personal projects.',
    includes: [
      'Team and individual headshots',
      'Event and conference coverage',
      'Office and workspace environmental portraits',
      'Brand and product photography',
      'Same-day turnaround available for events',
      'Full commercial licensing included',
    ],
  },
];

export default function Services() {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PageMeta
        title="Photography Services | Dhrumil Desai Photography Christchurch"
        description="Wedding photography, videography, engagement sessions, portrait photography, Indian weddings, and corporate photography across New Zealand. Based in Christchurch."
      />

      {/* ── Page Header ── */}
      <section className="pt-40 pb-20 px-6 md:px-12 bg-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <p
            className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-5 font-inter"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            What We Offer
          </p>
          <h1
            className="font-playfair text-4xl md:text-6xl text-charcoal leading-tight max-w-xl mx-auto"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Photography &amp; videography services
          </h1>
          <p
            className="font-inter font-light text-sm md:text-base text-gray-500 mt-6 max-w-md mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Every service is approached with the same quiet attention and artistic care,
            wherever you are in New Zealand.
          </p>
        </motion.div>
      </section>

      {/* ── Divider ── */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <hr className="border-t border-gray-100" />
      </div>

      {/* ── Service Cards ── */}
      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto space-y-0">
          {services.map((s, i) => (
            <FadeIn key={s.title} delay={0.05}>
              <div className={`py-16 ${i < services.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">

                  {/* Number + title */}
                  <div className="md:col-span-4">
                    <p
                      className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-4 font-inter"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {s.number}
                    </p>
                    <h2
                      className="font-playfair text-2xl md:text-3xl text-charcoal mb-3 leading-snug"
                      style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
                    >
                      {s.title}
                    </h2>
                    <p
                      className="font-inter font-light text-sm text-bronze"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {s.tagline}
                    </p>
                  </div>

                  {/* Description + includes */}
                  <div className="md:col-span-8">
                    <p
                      className="font-inter font-light text-sm md:text-base text-gray-600 leading-relaxed mb-8"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      {s.description}
                    </p>

                    <div>
                      <p
                        className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-4 font-inter"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                      >
                        What's included
                      </p>
                      <ul className="space-y-2">
                        {s.includes.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-3 font-inter font-light text-sm text-gray-600"
                            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                          >
                            <span className="w-1 h-1 rounded-full bg-bronze mt-2 flex-shrink-0" aria-hidden="true" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      to="/contact"
                      className="inline-block mt-10 text-[11px] tracking-[0.25em] uppercase font-inter text-bronze border-b border-bronze pb-0.5 hover:text-charcoal hover:border-charcoal transition-colors duration-200"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      Enquire About This Service
                    </Link>
                  </div>

                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── Contact CTA ── */}
      <section className="py-28 px-6 md:px-12 bg-cream text-center" aria-labelledby="services-cta">
        <FadeIn className="max-w-xl mx-auto">
          <h2
            id="services-cta"
            className="font-playfair text-3xl md:text-4xl text-charcoal mb-6 leading-tight"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Not sure which package is right for you?
          </h2>
          <p
            className="font-inter font-light text-sm text-gray-500 mb-10 leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Get in touch and Dhrumil will help you find the right coverage for your event,
            your vision, and your budget.
          </p>
          <Link
            to="/contact"
            className="border border-charcoal px-12 py-4 text-[11px] tracking-[0.25em] uppercase font-inter text-charcoal hover:bg-charcoal hover:text-white transition-all duration-300 inline-block"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Get in Touch
          </Link>
        </FadeIn>
      </section>
    </motion.main>
  );
}
